import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { EntityRef } from '../../components/EntityRef'
import { Icon } from '../../components/icons/Icon'
import { InlineAlert } from '../../components/InlineAlert'
import { StatusPill } from '../../components/StatusPill'
import {
  createChainOfTitle,
  createLoanOut,
  createPersonnel,
  createVendor,
  getProduction,
  listChainOfTitle,
  listLoanOuts,
  listPersonnel,
  listSpendLines,
  listVendors,
  switchRollup,
} from '../../api/film'
import { getUiConfig } from '../../api/identity'
import { ApiError } from '../../api/problem'
import { label } from '../../lib/labels'
import type {
  ChainOfTitleView,
  LoanOutView,
  PersonnelView,
  ProductionFYView,
  ProductionView,
  QualifiedSpendLineView,
  VendorView,
} from '../../types/film'
import { AiAssistPanel } from './AiAssistPanel'
import { ChainOfTitlePanel, LoanOutPanel, PersonnelPanel, VendorPanel } from './EntityPanels'
import { SpendGrid } from './SpendGrid'
import { SpendImportWizard } from './SpendImportWizard'

/**
 * `PS.FilmLabourVerify`, surfaced as the `/me/ui-config` component key `film.labour-verify`
 * (IB-155 design §5). Client-side UX only, not a security boundary — VisibilityCompiler registers
 * zero bindings and SecurityMatrixSeeder seeds only a representative subset today (honesty
 * caveat carried from Slice A/IB-42); a reviewer must not read "hidden in the UI" as "enforced
 * end-to-end". Defaults to hidden (fail-closed for the affordance, not the underlying data) if
 * `/me/ui-config` cannot be reached.
 */
const FILM_LABOUR_VERIFY_COMPONENT = 'film.labour-verify'

function formatPct(value: number | null): string {
  return value !== null ? `${value}%` : 'Not yet assessed'
}

const SECTION_STYLE = {
  padding: 'var(--iw-space-4)',
  background: 'var(--iw-surface)',
  border: '1px solid var(--iw-border)',
  borderRadius: 'var(--iw-radius-lg)',
} as const

/** AC5/AC6/AC7-12 — single-call header + phases + FY roll-up, then the SpendGrid (a second, separate scoped call). */
export function ProductionDetailScreen() {
  const { productionId } = useParams<{ productionId: string }>()
  const navigate = useNavigate()
  const [production, setProduction] = useState<ProductionView | null>(null)
  const [spendLines, setSpendLines] = useState<QualifiedSpendLineView[]>([])
  const [personnel, setPersonnel] = useState<PersonnelView[]>([])
  const [loanOuts, setLoanOuts] = useState<LoanOutView[]>([])
  const [vendors, setVendors] = useState<VendorView[]>([])
  const [chainOfTitle, setChainOfTitle] = useState<ChainOfTitleView[]>([])
  const [canVerify, setCanVerify] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [rollupError, setRollupError] = useState<string | null>(null)
  const [importOpen, setImportOpen] = useState(false)

  const load = useCallback(() => {
    if (!productionId) return
    setLoading(true)
    setError(null)
    setNotFound(false)
    Promise.all([
      getProduction(productionId),
      listSpendLines(productionId),
      listPersonnel(productionId),
      listLoanOuts(),
      listVendors(),
      listChainOfTitle(productionId),
    ])
      .then(([p, lines, personnelRows, loanOutRows, vendorRows, chainOfTitleRows]) => {
        setProduction(p)
        setSpendLines(lines)
        setPersonnel(personnelRows)
        setLoanOuts(loanOutRows)
        setVendors(vendorRows)
        setChainOfTitle(chainOfTitleRows)
      })
      .catch((err) => {
        if (err instanceof ApiError && err.problem.status === 404) {
          setNotFound(true)
          return
        }
        setError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not load this production.')
      })
      .finally(() => setLoading(false))

    // Capability-hide only (AC21, honesty caveat above) -- kept separate from the main load so a
    // ui-config failure never blocks the production itself from rendering; defaults to hidden.
    getUiConfig()
      .then((config) => setCanVerify(config.components.includes(FILM_LABOUR_VERIFY_COMPONENT)))
      .catch(() => setCanVerify(false))
  }, [productionId])

  useEffect(() => {
    load()
  }, [load])

  function handleRowUpdated(updated: QualifiedSpendLineView) {
    setSpendLines((prev) => prev.map((row) => (row.id === updated.id ? updated : row)))
  }

  async function handleSwitchRollup(fy: ProductionFYView, next: 'simple_avg' | 'cost_weighted') {
    if (!productionId || fy.authoritativeRollup === next) return
    setRollupError(null)
    try {
      const updated = await switchRollup(productionId, fy.fiscalYearId, next, fy.version)
      setProduction((prev) => (prev ? { ...prev, productionFys: prev.productionFys.map((f) => (f.id === updated.id ? updated : f)) } : prev))
    } catch (err) {
      setRollupError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not switch the roll-up.')
    }
  }

  async function handleCreatePersonnel(request: Parameters<typeof createPersonnel>[1]) {
    if (!productionId) return
    const created = await createPersonnel(productionId, request)
    setPersonnel((prev) => [...prev, created])
  }

  async function handleCreateLoanOut(request: Parameters<typeof createLoanOut>[0]) {
    const created = await createLoanOut(request)
    setLoanOuts((prev) => [...prev, created])
  }

  async function handleCreateVendor(request: Parameters<typeof createVendor>[0]) {
    const created = await createVendor(request)
    setVendors((prev) => [...prev, created])
  }

  async function handleCreateChainOfTitle(request: Parameters<typeof createChainOfTitle>[1]) {
    if (!productionId) return
    const created = await createChainOfTitle(productionId, request)
    setChainOfTitle((prev) => [...prev, created])
  }

  if (notFound) {
    return (
      <div style={{ padding: 'var(--iw-space-5)' }}>
        <InlineAlert severity="warning">This production could not be found — it may have been removed.</InlineAlert>
      </div>
    )
  }

  return (
    <div style={{ padding: 'var(--iw-space-5)', display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
      <div style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
        <Link to="/cases?kind=film" style={{ color: 'var(--iw-text-muted)' }}>
          Cases (Film & Media)
        </Link>{' '}
        › <EntityRef kind="production" id={productionId} name={production?.title} />
      </div>

      {error ? (
        <InlineAlert severity="error" action={<button type="button" className="iw-btn iw-btn--sm" onClick={() => navigate(0)}>Try again</button>}>
          {error}
        </InlineAlert>
      ) : loading ? (
        <p style={{ color: 'var(--iw-text-muted)' }}>Loading production…</p>
      ) : production ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-3)', flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--iw-accent)' }}>
              <Icon name="film" size={22} />
            </span>
            <h1 style={{ margin: 0, fontSize: 'var(--iw-size-xl)' }}>{production.title}</h1>
            <StatusPill variant="neutral" label={label('productionType', production.type)} />
          </div>

          <section
            style={{
              padding: 'var(--iw-space-4)',
              background: 'var(--iw-surface)',
              border: '1px solid var(--iw-border)',
              borderRadius: 'var(--iw-radius-lg)',
            }}
          >
            <h2 style={{ margin: '0 0 var(--iw-space-3)', fontSize: 'var(--iw-size-md)' }}>Phases</h2>
            {production.phases.length === 0 ? (
              <p style={{ color: 'var(--iw-text-muted)', margin: 0 }}>No phases recorded yet.</p>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', gap: 'var(--iw-space-2)', flexWrap: 'wrap' }}>
                {production.phases.map((phase) => (
                  <li key={phase.id}>
                    <StatusPill variant="info" label={`${label('productionPhase', phase.phase)} — ${phase.location}`} />
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section
            style={{
              padding: 'var(--iw-space-4)',
              background: 'var(--iw-surface)',
              border: '1px solid var(--iw-border)',
              borderRadius: 'var(--iw-radius-lg)',
            }}
          >
            <h2 style={{ margin: '0 0 var(--iw-space-3)', fontSize: 'var(--iw-size-md)' }}>Fiscal-year roll-up</h2>
            {rollupError ? <InlineAlert severity="error">{rollupError}</InlineAlert> : null}
            {production.productionFys.length === 0 ? (
              <p style={{ color: 'var(--iw-text-muted)', margin: 0 }}>No fiscal years recorded yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}>
                {production.productionFys.map((fy) => (
                  <div key={fy.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-4)', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>Simple average</span>
                      <strong>{formatPct(fy.qualifyingPctAvg)}</strong>
                      {fy.authoritativeRollup === 'simple_avg' ? <StatusPill variant="success" label="Authoritative" /> : (
                        <button type="button" className="iw-btn iw-btn--sm" onClick={() => void handleSwitchRollup(fy, 'simple_avg')}>
                          Use this instead
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>Cost-weighted</span>
                      <strong>{formatPct(fy.qualifyingPctEffective)}</strong>
                      {fy.authoritativeRollup === 'cost_weighted' ? <StatusPill variant="success" label="Authoritative" /> : (
                        <button type="button" className="iw-btn iw-btn--sm" onClick={() => void handleSwitchRollup(fy, 'cost_weighted')}>
                          Use this instead
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>Provisional credit</span>
                      <strong>{fy.creditProvisional !== null ? fy.creditProvisional : 'Not yet assessed'}</strong>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section
            style={{
              padding: 'var(--iw-space-4)',
              background: 'var(--iw-surface)',
              border: '1px solid var(--iw-border)',
              borderRadius: 'var(--iw-radius-lg)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)', marginBottom: 'var(--iw-space-3)' }}>
              <h2 style={{ margin: 0, fontSize: 'var(--iw-size-md)' }}>Qualified spend</h2>
              <span style={{ flex: 1 }} />
              <button type="button" className="iw-btn iw-btn--sm" onClick={() => setImportOpen(true)}>
                <Icon name="upload" size={14} /> Import spend lines
              </button>
            </div>
            <SpendGrid productionId={production.id} rows={spendLines} onRowUpdated={handleRowUpdated} />
            {importOpen ? (
              <SpendImportWizard
                productionId={production.id}
                fiscalYearIds={production.productionFys.map((fy) => fy.fiscalYearId)}
                onClose={() => setImportOpen(false)}
                onCommitted={() => void listSpendLines(production.id).then(setSpendLines)}
              />
            ) : null}
          </section>

          <section style={SECTION_STYLE}>
            <AiAssistPanel
              productionId={production.id}
              productionTitle={production.title}
              spendLines={spendLines}
              onAccepted={() => void listSpendLines(production.id).then(setSpendLines)}
            />
          </section>

          <section style={SECTION_STYLE}>
            <PersonnelPanel rows={personnel} canVerify={canVerify} onCreate={handleCreatePersonnel} />
          </section>

          <section style={SECTION_STYLE}>
            <LoanOutPanel
              rows={loanOuts}
              canVerify={canVerify}
              onCreate={handleCreateLoanOut}
              onRowUpdated={(updated) => setLoanOuts((prev) => prev.map((r) => (r.id === updated.id ? updated : r)))}
            />
          </section>

          <section style={SECTION_STYLE}>
            <VendorPanel
              rows={vendors}
              onCreate={handleCreateVendor}
              onRowUpdated={(updated) => setVendors((prev) => prev.map((r) => (r.id === updated.id ? updated : r)))}
            />
          </section>

          <section style={SECTION_STYLE}>
            <ChainOfTitlePanel
              productionId={production.id}
              rows={chainOfTitle}
              canVerify={canVerify}
              onCreate={handleCreateChainOfTitle}
              onRowUpdated={(updated) => setChainOfTitle((prev) => prev.map((r) => (r.id === updated.id ? updated : r)))}
            />
          </section>
        </>
      ) : null}
    </div>
  )
}
